import {
  inviteSupportByEmail,
  loadAdminUsers,
  loadSupportInvitations,
  revokeSupport,
  revokeSupportInvitation,
  setUserRole,
} from 'api/services/accessControlOperationsService'
import PageLoading from 'components/common/loading/PageLoading'
import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

const assignableRoles = ['tenant', 'owner', 'support'] as const

const AdminConsole = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [inviteEmail, setInviteEmail] = useState('')

  const usersQ = useQuery(['adminUsers'], loadAdminUsers)
  const invitesQ = useQuery(['supportInvitations'], loadSupportInvitations)

  const refreshUsers = () => queryClient.invalidateQueries(['adminUsers'])
  const refreshInvites = () => queryClient.invalidateQueries(['supportInvitations'])

  const setRoleMutation = useMutation(
    ({ userFk, role }: { userFk: string; role: string }) => setUserRole(userFk, role),
    {
      onSuccess: () => {
        toast.success(String(t('accessControl.adminConsole.roleUpdated')))
        refreshUsers()
      },
      onError: (e: unknown) => {
        toast.error(
          e instanceof Error ? e.message : String(t('accessControl.adminConsole.roleUpdateFailed'))
        )
      },
    }
  )

  const inviteMutation = useMutation((email: string) => inviteSupportByEmail(email), {
    onSuccess: data => {
      toast.success(
        data.data?.emailSent
          ? String(t('accessControl.adminConsole.inviteSent'))
          : String(t('accessControl.adminConsole.inviteCreatedNoEmail'))
      )
      setInviteEmail('')
      refreshInvites()
    },
    onError: (e: unknown) => {
      toast.error(
        e instanceof Error ? e.message : String(t('accessControl.adminConsole.inviteFailed'))
      )
    },
  })

  const revokeInviteMutation = useMutation((id: number) => revokeSupportInvitation(id), {
    onSuccess: () => {
      toast.success(String(t('accessControl.adminConsole.inviteRevoked')))
      refreshInvites()
    },
    onError: (e: unknown) => {
      toast.error(
        e instanceof Error ? e.message : String(t('accessControl.adminConsole.inviteRevokeFailed'))
      )
    },
  })

  const revokeSupportMutation = useMutation((userFk: string) => revokeSupport(userFk), {
    onSuccess: () => {
      toast.success(String(t('accessControl.adminConsole.supportRevoked')))
      refreshUsers()
    },
    onError: (e: unknown) => {
      toast.error(
        e instanceof Error ? e.message : String(t('accessControl.adminConsole.supportRevokeFailed'))
      )
    },
  })

  const handleInviteSubmit = (e: FormEvent) => {
    e.preventDefault()
    const email = inviteEmail.trim().toLowerCase()
    if (!email) return
    inviteMutation.mutate(email)
  }

  if (usersQ.isLoading) return <PageLoading />

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-imovSecondary">
          {t('accessControl.adminConsole.title')}
        </h1>
        <p className="text-sm text-gray-600">{t('accessControl.adminConsole.description')}</p>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
        <h2 className="text-sm font-semibold text-imovSecondary">
          {t('accessControl.adminConsole.inviteSupportTitle')}
        </h2>
        <p className="text-sm text-gray-600">
          {t('accessControl.adminConsole.inviteSupportDescription')}
        </p>
        <form onSubmit={handleInviteSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            placeholder={String(t('accessControl.adminConsole.inviteEmailPlaceholder'))}
            className="min-h-[40px] flex-1 rounded border border-gray-200 px-3 py-2 text-sm"
            required
          />
          <button
            type="submit"
            disabled={inviteMutation.isLoading || !inviteEmail.trim()}
            className="rounded bg-imovPrimary px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            {t('accessControl.adminConsole.sendInvite')}
          </button>
        </form>

        <div className="overflow-x-auto pt-2">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-3">{t('accessControl.labels.email')}</th>
                <th className="py-2 pr-3">{t('accessControl.labels.status')}</th>
                <th className="py-2 pr-3">{t('accessControl.adminConsole.expiresAt')}</th>
                <th className="py-2">{t('accessControl.labels.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(invitesQ.data ?? []).map(inv => (
                <tr key={inv.id} className="border-t border-gray-100">
                  <td className="py-2 pr-3">{inv.email}</td>
                  <td className="py-2 pr-3">{inv.status}</td>
                  <td className="py-2 pr-3">{inv.expires_at}</td>
                  <td className="py-2">
                    {inv.status === 'pending' ? (
                      <button
                        type="button"
                        className="rounded border border-gray-200 px-2 py-1"
                        onClick={() => revokeInviteMutation.mutate(inv.id)}
                      >
                        {t('accessControl.adminConsole.revokeInvite')}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
              {!invitesQ.isLoading && (invitesQ.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-3 text-gray-500">
                    {t('accessControl.adminConsole.noInvites')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <div className="rounded-xl border border-gray-200 bg-white p-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-2 pr-3">{t('accessControl.labels.user')}</th>
              <th className="py-2 pr-3">{t('accessControl.labels.email')}</th>
              <th className="py-2 pr-3">{t('accessControl.labels.source')}</th>
              <th className="py-2 pr-3">{t('accessControl.labels.role')}</th>
              <th className="py-2">{t('accessControl.labels.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {(usersQ.data ?? []).map((u: any) => {
              const isAdmin = u.role === 'admin'
              return (
                <tr key={u.userFk} className="border-t border-gray-100">
                  <td className="py-2 pr-3">{u.name || u.userFk}</td>
                  <td className="py-2 pr-3">{u.email || '—'}</td>
                  <td className="py-2 pr-3">{u.source}</td>
                  <td className="py-2 pr-3">
                    {isAdmin ? (
                      <span className="font-medium">admin</span>
                    ) : (
                      <select
                        value={
                          (assignableRoles as readonly string[]).includes(u.role)
                            ? u.role
                            : 'tenant'
                        }
                        onChange={e =>
                          setRoleMutation.mutate({ userFk: u.userFk, role: e.target.value })
                        }
                        className="rounded border border-gray-200 px-2 py-1"
                      >
                        {assignableRoles.map(r => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="py-2">
                    {u.role === 'support' ? (
                      <button
                        type="button"
                        className="rounded border border-gray-200 px-2 py-1"
                        onClick={() => revokeSupportMutation.mutate(u.userFk)}
                      >
                        {t('accessControl.adminConsole.revokeSupport')}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AdminConsole
